// src/pages/EditSystemPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, storage } from "../firebase/firebase-config";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Container, Row, Col, Form, Button, Card, Alert, Spinner } from "react-bootstrap";
import { FaArrowLeft, FaSave, FaStar, FaTimes, FaUpload, FaGraduationCap, FaChalkboardTeacher, FaLink, FaFileAlt } from "react-icons/fa";

function EditSystemPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",               // Course name
    courseNumber: "",        // Course number
    yearTaken: new Date().getFullYear(),  // Default to current year
    professor: "",           // Professor/instructor
    description: "",         // Course description
    imageUrl: "",            // Course cover image URL
    courseWebsite: "",       // Course website URL
    courseNotesUrl: "",      // Course notes URL
    rating: 0,               // Course rating
    tags: [],                // Course tags/categories
    status: "upcoming",      // Default to "Upcoming" (upcoming, inprogress, completed)
    program: "PBD-CS",       // Default to PBD-CS
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ image: 0 });
  const [isUploading, setIsUploading] = useState({ image: false });
  const [tagInput, setTagInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null); // Added missing state for success message

  // Fetch course data on component mount
  useEffect(() => {
    const fetchCourseData = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const docRef = doc(db, "books", id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const courseData = docSnap.data();
          setFormData({
            title: courseData.title || "",
            courseNumber: courseData.courseNumber || "",
            yearTaken: courseData.yearTaken || new Date().getFullYear(),
            professor: courseData.professor || courseData.author || "",
            description: courseData.description || "",
            imageUrl: courseData.imageUrl || "",
            courseWebsite: courseData.courseWebsite || courseData.websiteLink || "",
            courseNotesUrl: courseData.courseNotesUrl || courseData.readNowLink || "",
            rating: courseData.rating || 0,
            tags: courseData.tags || [],
            status: courseData.status || courseData.readingStatus || "upcoming",
            program: courseData.program || "PBD-CS",
          });
          
          console.log("Course data loaded:", courseData);
        } else {
          console.log("No such document!");
          setError("Course not found");
        }
      } catch (err) {
        console.error("Error fetching course data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCourseData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImageToFirebase = async (file) => {
    if (!file) return null;
    
    try {
      setIsUploading(prev => ({ ...prev, image: true }));
      setUploadProgress(prev => ({ ...prev, image: 0 }));
      
      // Create a unique filename with timestamp and original file extension
      const fileExtension = file.name.split('.').pop();
      const fileName = `course_images/${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExtension}`;
      
      // Create a reference to the storage location
      const storageRef = ref(storage, fileName);
      
      // Upload the file
      console.log('Starting upload of image file:', file.name);
      const uploadTask = await uploadBytes(storageRef, file);
      setUploadProgress(prev => ({ ...prev, image: 50 }));
      
      // Get the download URL
      console.log('Upload complete, getting download URL');
      const downloadURL = await getDownloadURL(uploadTask.ref);
      console.log('Download URL:', downloadURL);
      setUploadProgress(prev => ({ ...prev, image: 100 }));
      
      return downloadURL;
    } catch (error) {
      console.error("Error uploading image:", error);
      console.error("Error details:", error.code, error.message);
      throw new Error(`Failed to upload image: ${error.message}`);
    } finally {
      setIsUploading(prev => ({ ...prev, image: false }));
    }
  };

  const handleRatingChange = (rating) => {
    setFormData({
      ...formData,
      rating
    });
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (tagInput.trim() !== "" && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Upload image if selected
      let updatedImageUrl = formData.imageUrl;
      
      if (imageFile) {
        updatedImageUrl = await uploadImageToFirebase(imageFile);
      }
      
      // Create course update data object with backward compatibility
      const courseUpdateData = {
        ...formData,
        imageUrl: updatedImageUrl,
        rating: parseInt(formData.rating) || 0,
        yearTaken: parseInt(formData.yearTaken) || new Date().getFullYear(),
        readingStatus: formData.status, // Map to existing field for backward compatibility
        websiteLink: formData.courseWebsite, // For backward compatibility
        readNowLink: formData.courseNotesUrl, // For backward compatibility
        author: formData.professor, // Map professor to author for backward compatibility
        updatedAt: serverTimestamp()
      };
      
      // Update document in Firestore
      const docRef = doc(db, "books", id);
      await updateDoc(docRef, courseUpdateData);
      
      console.log("Course updated successfully");
      setSuccessMessage("Course updated successfully!");
      
      // Navigate back after short delay
      setTimeout(() => {
        navigate("/");
      }, 1500);
    } catch (err) {
      console.error("Error updating course:", err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Rating component
  const RatingSelector = ({ value, onChange }) => {
    return (
      <div className="d-flex align-items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            className={`star-rating ${star <= value ? 'text-warning' : 'text-muted'}`}
            style={{ cursor: 'pointer', fontSize: '1.5rem', marginRight: '0.25rem' }}
            onClick={() => onChange(star)}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <main className="main-content" style={{ marginLeft: 0 }}>
          <Container className="py-5">
            <div className="text-center">
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-3">Loading course data...</p>
            </div>
          </Container>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="main-content" style={{ marginLeft: 0 }}>
        <Container className="py-4">
          <Row className="justify-content-center">
            <Col md={10} lg={8}>
              <div className="mb-4">
                <Button 
                  variant="link" 
                  className="p-0 text-decoration-none" 
                  onClick={() => navigate("/")}
                >
                  <FaArrowLeft className="me-2" />
                  Back to Dashboard
                </Button>
              </div>

              <Card className="shadow-sm border-0">
                <Card.Header className="bg-primary text-white">
                  <h4 className="mb-0">Edit Course</h4>
                </Card.Header>
                <Card.Body>
                  {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}

                  {successMessage && (
                    <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
                      {successMessage}
                    </Alert>
                  )}

                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" role="status" variant="primary">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                      <p className="mt-3">Loading course data...</p>
                    </div>
                  ) : (
                    <Form onSubmit={handleUpdate}>
                      <Row>
                        <Col md={8}>
                          <Form.Group className="mb-3">
                            <Form.Label>Course Name</Form.Label>
                            <Form.Control
                              type="text"
                              name="title"
                              value={formData.title}
                              onChange={handleChange}
                              placeholder="Enter course name"
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Course Number</Form.Label>
                            <Form.Control
                              type="text"
                              name="courseNumber"
                              value={formData.courseNumber}
                              onChange={handleChange}
                              placeholder="e.g. CS101"
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Professor/Instructor</Form.Label>
                            <Form.Control
                              type="text"
                              name="professor"
                              value={formData.professor}
                              onChange={handleChange}
                              placeholder="Enter professor name"
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Year Taken</Form.Label>
                            <Form.Control
                              type="number"
                              name="yearTaken"
                              value={formData.yearTaken}
                              onChange={handleChange}
                              placeholder="YYYY"
                              min="1900"
                              max="2099"
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* Course Status Field */}
                      <Form.Group className="mb-4">
                        <Form.Label>Course Status</Form.Label>
                        <div className="d-flex flex-wrap gap-3">
                          <Form.Check
                            type="radio"
                            id="status-upcoming"
                            name="status"
                            value="upcoming"
                            checked={formData.status === "upcoming"}
                            onChange={handleChange}
                            label="Upcoming"
                            className="me-3"
                          />
                          <Form.Check
                            type="radio"
                            id="status-inprogress"
                            name="status"
                            value="inprogress"
                            checked={formData.status === "inprogress"}
                            onChange={handleChange}
                            label="In Progress"
                            className="me-3"
                          />
                          <Form.Check
                            type="radio"
                            id="status-completed"
                            name="status"
                            value="completed"
                            checked={formData.status === "completed"}
                            onChange={handleChange}
                            label="Completed"
                          />
                        </div>
                      </Form.Group>

                      {/* Program Selection Field */}
                      <Form.Group className="mb-4">
                        <Form.Label>Program</Form.Label>
                        <div className="d-flex flex-wrap gap-3">
                          <Form.Check
                            type="radio"
                            id="program-pbdcs"
                            name="program"
                            value="PBD-CS"
                            checked={formData.program === "PBD-CS"}
                            onChange={handleChange}
                            label="PBD-CS"
                            className="me-3"
                          />
                          <Form.Check
                            type="radio"
                            id="program-pddmarketing"
                            name="program"
                            value="PDD-Marketing"
                            checked={formData.program === "PDD-Marketing"}
                            onChange={handleChange}
                            label="PDD-Marketing"
                            className="me-3"
                          />
                          <Form.Check
                            type="radio"
                            id="program-bachelorenglish"
                            name="program"
                            value="Bachelor-English"
                            checked={formData.program === "Bachelor-English"}
                            onChange={handleChange}
                            label="Bachelor-English"
                          />
                        </div>
                      </Form.Group>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Course Cover Image</Form.Label>
                            
                            {formData.imageUrl && !imagePreview && (
                              <div className="mb-3 border rounded p-2 text-center">
                                <img 
                                  src={formData.imageUrl} 
                                  alt="Current" 
                                  style={{ maxWidth: '100%', maxHeight: '150px' }}
                                />
                                <div className="mt-2 small text-muted">Current image</div>
                              </div>
                            )}
                            
                            <div className="d-flex flex-column">
                              <div className="mb-2">
                                <input
                                  type="file"
                                  id="imageUpload"
                                  accept="image/*"
                                  onChange={handleImageChange}
                                  className="d-none"
                                />
                                <label htmlFor="imageUpload" className="btn btn-outline-primary mb-2 w-100">
                                  <FaUpload className="me-2" /> Choose New Image
                                </label>
                              </div>
                              
                              {imagePreview && (
                                <div className="mb-3 border rounded p-2 text-center">
                                  <img 
                                    src={imagePreview} 
                                    alt="New" 
                                    style={{ maxWidth: '100%', maxHeight: '150px' }}
                                  />
                                  <div className="mt-2 small text-muted">New image</div>
                                </div>
                              )}
                              
                              {isUploading.image && (
                                <div className="progress mb-2">
                                  <div 
                                    className="progress-bar" 
                                    role="progressbar" 
                                    style={{ width: `${uploadProgress.image}%` }}
                                    aria-valuenow={uploadProgress.image} 
                                    aria-valuemin="0" 
                                    aria-valuemax="100"
                                  >
                                    {uploadProgress.image}%
                                  </div>
                                </div>
                              )}
                              
                              {imageFile && (
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="small text-muted">
                                    {imageFile.name}
                                  </span>
                                  <Button 
                                    variant="link" 
                                    className="text-danger p-0"
                                    onClick={() => {
                                      setImageFile(null);
                                      setImagePreview(null);
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              )}
                              
                              <Form.Control
                                type="url"
                                name="imageUrl"
                                value={imageFile ? "" : formData.imageUrl}
                                onChange={handleChange}
                                placeholder="https://example.com/image.jpg"
                                className="mt-2"
                                disabled={imageFile !== null}
                              />
                              <Form.Text className="text-muted">
                                Enter an image URL or upload a new image
                              </Form.Text>
                            </div>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Course Website URL</Form.Label>
                            <Form.Control
                              type="url"
                              name="courseWebsite"
                              value={formData.courseWebsite}
                              onChange={handleChange}
                              placeholder="https://example.com/course"
                            />
                            <Form.Text className="text-muted">
                              Link to the course website or syllabus
                            </Form.Text>
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <Form.Label>Course Notes URL</Form.Label>
                            <Form.Control
                              type="url"
                              name="courseNotesUrl"
                              value={formData.courseNotesUrl}
                              onChange={handleChange}
                              placeholder="https://example.com/notes"
                            />
                            <Form.Text className="text-muted">
                              Link to your course notes or resources
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3">
                        <Form.Label>Course Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          placeholder="Enter course description"
                        />
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>Rating</Form.Label>
                        <div>
                          <RatingSelector 
                            value={formData.rating} 
                            onChange={handleRatingChange} 
                          />
                        </div>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>Tags</Form.Label>
                        <div className="d-flex mb-2">
                          <Form.Control
                            type="text"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddTag(e);
                              }
                            }}
                            placeholder="Add a tag (e.g. Computer Science, Math)"
                            className="me-2"
                          />
                          <Button 
                            variant="outline-secondary" 
                            onClick={handleAddTag}
                          >
                            Add
                          </Button>
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {formData.tags && formData.tags.length > 0 ? (
                            formData.tags.map((tag, index) => (
                              <div 
                                key={index} 
                                className="badge bg-light text-dark d-flex align-items-center p-2"
                              >
                                {tag}
                                <FaTimes 
                                  className="ms-2" 
                                  onClick={() => handleRemoveTag(tag)}
                                  style={{ cursor: 'pointer' }}
                                />
                              </div>
                            ))
                          ) : (
                            <div className="text-muted small">No tags added yet</div>
                          )}
                        </div>
                      </Form.Group>

                      <div className="d-grid gap-2 mt-4">
                        <Button 
                          variant="primary" 
                          type="submit" 
                          disabled={isSubmitting || isUploading.image}
                          className="d-flex align-items-center justify-content-center gap-2"
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner size="sm" animation="border" role="status" />
                              <span className="ms-2">Updating...</span>
                            </>
                          ) : (
                            <>
                              <FaSave /> Save Changes
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </main>
    </div>
  );
}

export default EditSystemPage;